import api from '@/lib/axios';
import type {
    AuthResponse,
    LoginCredentials,
    RegisterData,
    User,
} from '@/types';

export const authService = {
  /**
   * Login user with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/login', credentials);
    return response.data;
  },

  /**
   * Register new user
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    // Transform the data to match backend expectations
    const [firstName, ...lastNameParts] = data.full_name.trim().split(' ');
    const lastName = lastNameParts.join(' ') || firstName; // Use firstName as lastName if only one name provided

    const backendData = {
      email: data.email,
      password: data.password,
      first_name: firstName,
      last_name: lastName,
      tenant_name: data.tenant_name,
    };

    const response = await api.post<AuthResponse>(
      '/auth/register',
      backendData
    );
    return response.data;
  },

  /**
   * Refresh access token
   */
  async refresh(refreshToken: string): Promise<{ access_token: string }> {
    const response = await api.post<{ access_token: string }>(
      '/auth/refresh',
      {},
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      }
    );
    return response.data;
  },

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<User> {
    const response = await api.get<User>('/auth/me');
    return response.data;
  },

  /**
   * Logout user (client-side only, no backend endpoint needed for JWT)
   */
  logout(): void {
    // JWT tokens are stateless, so we just clear client storage
    // The authStore will handle clearing the state
  },

  /**
   * Request a password reset email
   */
  async forgotPassword(email: string): Promise<void> {
    await api.post('/auth/forgot-password', { email });
  },

  /**
   * Reset password using a token received by email
   */
  async resetPassword(token: string, password: string): Promise<void> {
    await api.post('/auth/reset-password', { token, password });
  },
};
